if (!customElements.get('integrated-header-hero')) {
  customElements.define(
    'integrated-header-hero',
    class IntegratedHeaderHero extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('[data-header]');
        this.drawer = this.querySelector('[data-drawer]');
        this.search = this.querySelector('[data-search]');
        this.searchInput = this.querySelector('[data-search-input]');
        this.predictiveSearch = this.querySelector('predictive-search');
        this.searchTrigger = this.querySelector('[data-open-search]');
        this.menuItems = Array.from(this.querySelectorAll('[data-menu-item]'));
        this.mobileMedia = window.matchMedia('(max-width: 989px)');

        this.querySelectorAll('[data-open-search]').forEach((button) => {
          button.addEventListener('click', () => this.openSearch());
        });
        this.querySelector('[data-close-search]')?.addEventListener('click', () => this.closeSearch());
        this.querySelector('[data-open-drawer]')?.addEventListener('click', () => this.openDrawer());
        this.querySelectorAll('[data-close-drawer]').forEach((button) => {
          button.addEventListener('click', () => this.closeDrawer());
        });

        this.menuItems.forEach((item) => {
          const trigger = item.querySelector('[data-menu-trigger]');
          if (!trigger) return;

          item.addEventListener('mouseenter', () => this.openMenu(item));
          item.addEventListener('mouseleave', () => this.closeMenu(item));
          trigger.addEventListener('focus', () => this.openMenu(item));
          trigger.addEventListener('click', (event) => {
            if (item.querySelector('[data-mega]')) {
              event.preventDefault();
              item.classList.contains('is-active') ? this.closeMenu(item) : this.openMenu(item);
            }
          });
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          this.closeSearch();
          this.closeDrawer();
          this.closeAllMenus();
        });

        document.addEventListener('pointerdown', (event) => {
          if (!this.classList.contains('is-search-open') || this.mobileMedia.matches) return;
          if (this.search?.contains(event.target) || event.target.closest('[data-open-search]')) return;
          this.closeSearch();
        });

        this.searchInput?.addEventListener('input', () => {
          this.classList.toggle('has-search-query', this.searchInput.value.trim().length > 0);
        });

        this.onScroll = () => {
          this.updateStickyState();
          if (this.classList.contains('is-search-open')) this.updateSearchPosition();
        };
        this.onResize = () => {
          this.updateStickyState();
          if (this.classList.contains('is-search-open')) this.updateSearchPosition();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        this.updateStickyState();
      }

      openMenu(item) {
        this.closeAllMenus(item);
        item.classList.add('is-active');
        item.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'true');
        this.classList.add('is-menu-open');
      }

      closeMenu(item) {
        item.classList.remove('is-active');
        item.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
        if (!this.querySelector('[data-menu-item].is-active')) {
          this.classList.remove('is-menu-open');
        }
      }

      closeAllMenus(exception) {
        this.menuItems.forEach((item) => {
          if (item !== exception) this.closeMenu(item);
        });
      }

      openDrawer() {
        this.closeSearch();
        this.classList.add('is-drawer-open');
        document.body.classList.add('integrated-header-hero-lock');
        this.drawer?.setAttribute('aria-hidden', 'false');
        this.querySelector('[data-close-drawer]')?.focus();
      }

      closeDrawer() {
        if (!this.classList.contains('is-drawer-open')) return;
        this.classList.remove('is-drawer-open');
        this.drawer?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('integrated-header-hero-lock');
        this.querySelector('[data-open-drawer]')?.focus();
      }

      openSearch() {
        this.closeDrawer();
        this.closeAllMenus();
        this.updateSearchPosition();
        this.classList.add('is-search-open');
        if (this.mobileMedia.matches) {
          document.body.classList.add('integrated-header-hero-lock');
        }
        this.search?.setAttribute('aria-hidden', 'false');
        window.requestAnimationFrame(() => this.searchInput?.focus());
      }

      closeSearch() {
        if (!this.classList.contains('is-search-open')) return;
        this.classList.remove('is-search-open', 'has-search-query');
        this.search?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('integrated-header-hero-lock');
        if (this.searchInput) {
          this.searchInput.value = '';
          this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        this.predictiveSearch?.close?.(true);
        this.querySelector('[data-open-search]')?.focus();
      }

      updateSearchPosition() {
        if (!this.searchTrigger || this.mobileMedia.matches) return;
        const triggerRect = this.searchTrigger.getBoundingClientRect();
        const sideGutter = 32;
        const preferredPanelWidth = this.classList.contains('is-stuck') ? 720 : 900;
        const panelWidth = Math.min(preferredPanelWidth, window.innerWidth - sideGutter * 2);
        const fieldWidth = this.classList.contains('is-stuck') ? triggerRect.width : Math.min(triggerRect.width, panelWidth);
        const fieldLeft = this.classList.contains('is-stuck') ? triggerRect.left : Math.min(Math.max(triggerRect.left, sideGutter), window.innerWidth - sideGutter - panelWidth);
        const resultsLeft = Math.min(Math.max(fieldLeft, sideGutter), window.innerWidth - sideGutter - panelWidth);
        this.style.setProperty('--search-left', `${fieldLeft}px`);
        this.style.setProperty('--search-results-left', `${resultsLeft - fieldLeft}px`);
        this.style.setProperty('--search-top', `${triggerRect.top}px`);
        this.style.setProperty('--search-field-width', `${fieldWidth}px`);
        this.style.setProperty('--search-panel-width', `${panelWidth}px`);
      }

      updateStickyState() {
        if (this.mobileMedia.matches) {
          this.classList.remove('is-stuck');
          return;
        }
        this.classList.toggle('is-stuck', window.scrollY > 120);
      }
    }
  );
}
